/**
 * Collapse a facility's available spaces into the distinct products it rents.
 *
 * Pure and free of JSX so it can be exercised directly against a real payload;
 * the icons for each feature live with the markup that renders them.
 */

/** Boolean columns worth advertising, in the order they should be shown. */
export const SPACE_FEATURES = [
  ['is_pull_through', 'Pull-through'],
  ['has_power', 'Power'],
  ['has_water', 'Water'],
  ['has_sewer', 'Sewer'],
];

/**
 * Keyed on the three things a visitor is choosing between — cover, length and
 * width. Rate is deliberately not in the key: two 45ft covered spaces at $260
 * and $275 are one product at two prices, and splitting them would advertise
 * the yard as smaller and dearer than it is. The cheapest is shown.
 *
 * A feature survives only if every space in the group has it, and the clearance
 * shown is the lowest in the group. Both are the same rule — a card must
 * promise what the visitor is guaranteed, not what they might happen to get,
 * because the reservation that follows picks whichever space is free.
 */
export function groupSpaces(units) {
  const byShape = new Map();

  for (const u of units ?? []) {
    if (u.status !== 'available') continue;

    const key = `${u.space_type}|${Number(u.length_ft)}|${Number(u.width_ft)}`;
    const clearance = u.height_clearance_ft == null ? null : Number(u.height_clearance_ft);
    const existing = byShape.get(key);

    if (!existing) {
      byShape.set(key, {
        key,
        space_type: u.space_type,
        length_ft: Number(u.length_ft),
        width_ft: Number(u.width_ft),
        height_clearance_ft: clearance,
        min_rate_cents: Number(u.monthly_rate_cents),
        count: 1,
        featureKeys: SPACE_FEATURES.filter(([f]) => Boolean(u[f])).map(([f]) => f),
      });
      continue;
    }

    existing.count += 1;
    existing.min_rate_cents = Math.min(existing.min_rate_cents, Number(u.monthly_rate_cents));
    existing.featureKeys = existing.featureKeys.filter((f) => Boolean(u[f]));
    if (clearance != null) {
      existing.height_clearance_ft = existing.height_clearance_ft == null
        ? clearance
        : Math.min(existing.height_clearance_ft, clearance);
    }
  }

  return [...byShape.values()].sort((a, b) => a.min_rate_cents - b.min_rate_cents);
}
